/* eslint-disable import/prefer-default-export */
const {
  nbc: {
    brand,
  },
} = window;

interface Byline {
  display_name: string;
  url: string;
  rsn_is_insider: boolean;
  rsn_insider_team?: string;
}

export interface CardTypes {
  network_object_id: string;
  is_pinned: boolean;
  date_time: string;
  liveBlogDate: {
    string: string,
  };
  bylines?: Byline[];
  hide_title: boolean;
  summary?: string;
  link: string;
  share_links: {
    facebook: string;
    twitter: string;
    email: string;
    native: string;
  };
  title: string;
  type: string;
  eyebrow: {
    link: string;
    label: string;
    sponsored: string;
  };
  excerpt_link_display: {
    title: string;
    link: string;
  };
  date_string: string;
  image: {
    alt_text: string,
    caption: {
      rendered: string;
    };
    media_details: {
      image_meta: {
        credit: string;
      };
    };
    ratios: {
      '16:9': {
        850: string;
      };
    };
  },
  is_failover_image: boolean,
  content_blocks: {
    blockName: string;
    rendered: string;
    attrs: {
      providerNameSlug: string;
      title: string;
      galleryId?: string;
    };
    slides?,
  }[],
  singular_hero: {
    type: string,
    url: string,
    caption: string,
    thumbnail: string,
    credit: string,
  },
  date: string,
  video: {
    id: string;
    url: string;
    title: string;
    meta: {
      video_is_fast: string;
      syndicated_id: string;
      show: string;
      event: string;
      genre: string;
      m3u8_url: string;
      video_id: string;
      media_pid: string;
      video_length: number;
      mpx_is_livestream: string;
      mpx_m3upid: string;
      video_call_letter: string;
      mp4_url: string;
      nbc_syndication_category_name: string;
      nbc_primary_category_id: number;
    };
    sport_slug: string;
    league: string;
    team: {
      sport: string;
      league: string;
      title: string;
    };
    custom_data: {
      Source: string;
      sentiment: string;
      topic: string;
    };
    disableEmbed: boolean;
    category_names: string[];
    tag_names: string[];
  };
  originating_call_letters: string;
  sponsor: {
    name: string;
  };
  canonical_url: string;
  object_sensitivity: string;
}

/**
 * #region Get published date and return formatted card/post date
 *
 * @param {String} date published date
 * @param {String} marketTimezone market timezone
 * @return {Object} agoTime, timeFormatted
 */
export const getFormattedDate = (date: string, marketTimezone: string) => {
  // time logic with timezone
  const cardDate = new Date(date);
  const cardTime = cardDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true, timeZone: marketTimezone });
  const cardTimeZone = cardDate.toLocaleTimeString('en-US', { timeZoneName: 'short', timeZone: marketTimezone }).split(' ')[2];
  const timeFormatted = `${cardTime} ${cardTimeZone}`;

  // ago logic
  const nowDate = new Date();
  const timeDifference = Math.abs(nowDate.getTime() - cardDate.getTime());
  const secondDifference = Math.floor(timeDifference / 1000);
  let minuteDifference = Math.floor(timeDifference / (1000 * 60));
  const hourDifference = Math.floor(timeDifference / (1000 * 3600));
  const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));

  const locale = 'telemundo' === brand ? 'es' : 'en-US';
  const tidyMonth = cardDate.toLocaleDateString(locale, { month: 'short', timeZone: marketTimezone }).toUpperCase();
  const tidyDay = cardDate.toLocaleDateString(locale, { day: 'numeric', timeZone: marketTimezone });
  let tidyYear = cardDate.toLocaleDateString(locale, { year: 'numeric', timeZone: marketTimezone });
  tidyYear = tidyYear === nowDate.getFullYear().toString() ? '' : `, ${tidyYear}`;
  const tidyDate = `${tidyMonth} ${tidyDay}${tidyYear}`;

  let minText = 119 < secondDifference ? 'MINS AGO' : 'MIN AGO';
  minuteDifference = 0 < minuteDifference ? minuteDifference : 1;

  if ('telemundo' === brand) {
    minText = 119 < secondDifference ? 'MINUTOS' : 'MINUTO';
  }

  let agoTime = 'telemundo' === brand ? `HACE ${minuteDifference} ${minText}` : `${minuteDifference} ${minText}`;

  if (59 < minuteDifference) {
    let hourText = 119 < minuteDifference ? 'HOURS AGO' : 'HOUR AGO';
    if ('telemundo' === brand) {
      hourText = 119 < minuteDifference ? 'HORAS' : 'HORA';
    }

    agoTime = 'telemundo' === brand ? `HACE ${hourDifference} ${hourText}` : `${hourDifference} ${hourText}`;
  }

  if (24 < hourDifference) {
    let dayText = 47 < hourDifference ? 'DAYS AGO' : 'DAY AGO';
    if ('telemundo' === brand) {
      dayText = 47 < hourDifference ? 'DÍAS' : 'DÍA';
    }

    agoTime = 'telemundo' === brand ? `HACE ${daysDifference} ${dayText}` : `${daysDifference} ${dayText}`;
  }

  return { agoTime, timeFormatted, tidyDate };
};

/**
 * #region Scroll to function
 *
 * @param {string} elementId class or id of the element to scroll to
 */
export const scrollToItem = (elementId: string, cardscroll: boolean) => {
  const leaderboardAd = document.querySelector('.leaderboard-ad');
  const leaderboardAdOffset = leaderboardAd ? leaderboardAd.clientHeight - 125 : 125;
  const deviceOffset = window.matchMedia('screen and (max-width: 767px)').matches ? 75 : leaderboardAdOffset;
  const scrollTypeOffset = cardscroll ? deviceOffset : 100;
  const elementContainer = document.querySelector(elementId);

  if (elementContainer) {
    setTimeout(() => {
      const elementContainerTop = elementContainer.getBoundingClientRect().top;
      window.scrollTo({
        top: (window.pageYOffset - scrollTypeOffset) + elementContainerTop,
        behavior: 'smooth',
      });
    }, 2000);
  }
};

/**
 * #region Check if screen width is Mobile format
 */
export const isMobile = window.matchMedia('screen and (max-width: 767px)').matches;

/**
 * Check if string is valid URL
 *
 * @param {string} str the string to be evaluated
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
export const isValidURL = (str: string) => {
  try {
    const testURL = new URL(str);
  } catch (error) {
    return false;
  }
  return true;
};
/* eslint-enable @typescript-eslint/no-unused-vars */
